import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ClockInButton, ClockOutButton } from '../components/ClockButtons';
import { useLocation } from '../context/LocationContext';
import { Layout } from '../components/Layout';
import { timeClockAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatTime, formatDate, calculateHours } from '../lib/utils';
import {
  Clock,
  Play,
  Square,
  Loader2,
  Check,
  X,
  History,
  Pencil,
  DollarSign,
} from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function TimeClock() {
  const { user, canManage } = useAuth();
  const { selectedLocationId, isAllLocations } = useLocation();
  const activeLocId = isAllLocations ? user?.restaurant_id : selectedLocationId;
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [activeShift, setActiveShift] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [pendingShifts, setPendingShifts] = useState([]);
  const [actionLoading, setActionLoading] = useState(null); // shiftId being acted on
  const [editState, setEditState] = useState(null); // {id, clockIn, clockOut} or null
  const [empSummary, setEmpSummary] = useState([]);
  const [deductModal, setDeductModal] = useState(null); // {employeeId, employeeName, amount}

  useEffect(() => {
    fetchData();
    if (canManage) fetchEmpSummary();
  }, [user, activeLocId]);

  const fetchData = async () => {
    try {
      const [activeRes, shiftsRes] = await Promise.all([
        timeClockAPI.getActiveShift(),
        timeClockAPI.getShifts({ restaurant_id: activeLocId }),
      ]);

      setActiveShift(activeRes.data);
      
      const allShifts = shiftsRes.data || [];
      setShifts(allShifts.filter(s => s.status !== 'pending_approval').slice(0, 20));
      setPendingShifts(allShifts.filter(s => s.status === 'pending_approval'));
    } catch (error) {
      console.error('Error fetching time clock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpSummary = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/attendance/employee-summary`, { headers: authHeaders() });
      setEmpSummary(data.filter(e => e.advance_balance > 0));
    } catch (err) {
      console.error('Failed to load employee summary', err);
    }
  };

  const handleApplyDeduction = async () => {
    if (!deductModal?.employeeId || !deductModal?.amount) return;
    try {
      await axios.post(`${API_BASE}/advances/apply-deduction`, {
        employee_id: deductModal.employeeId,
        amount: parseFloat(deductModal.amount),
      }, { headers: authHeaders() });
      toast.success('Deduction applied');
      setDeductModal(null);
      fetchEmpSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply deduction');
    }
  };

  const handleClockAction = async () => {
    setClockLoading(true);
    try {
      if (activeShift) {
        await timeClockAPI.clockOut();
        toast.success('Clocked out successfully!');
        setActiveShift(null);
      } else {
        const response = await timeClockAPI.clockIn();
        toast.success('Clocked in successfully!');
        setActiveShift({ start_time: response.data.start_time });
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Clock action failed');
    } finally {
      setClockLoading(false);
    }
  };

  const handleApprove = async (shiftId) => {
    setActionLoading(shiftId);
    try {
      await axios.post(`${API_BASE}/attendance/approve`, { record_id: shiftId, action: 'approve' }, { headers: authHeaders() });
      toast.success('Shift approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve shift');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (shiftId) => {
    setActionLoading(shiftId);
    try {
      await axios.post(`${API_BASE}/attendance/approve`, { record_id: shiftId, action: 'reject' }, { headers: authHeaders() });
      toast.success('Shift rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject shift');
    } finally {
      setActionLoading(null);
    }
  };

  const openEdit = (shift) => {
    const toLocal = (iso) => iso ? iso.slice(0, 16) : '';
    setEditState({ id: shift.id, clockIn: toLocal(shift.start_time), clockOut: toLocal(shift.end_time) });
  };

  const handleUpdate = async () => {
    if (!editState) return;
    try {
      await axios.post(`${API_BASE}/attendance/update`, {
        record_id: editState.id,
        clock_in_time:  editState.clockIn  ? editState.clockIn  + ':00' : undefined,
        clock_out_time: editState.clockOut ? editState.clockOut + ':00' : undefined,
      }, { headers: authHeaders() });
      toast.success('Times updated');
      setEditState(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update times');
    }
  };

  const getElapsedTime = () => {
    if (!activeShift?.start_time) return '0:00';
    const start = new Date(activeShift.start_time);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const statusBadge = (status) => {
    if (status === 'approved') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Approved</span>;
    if (status === 'rejected') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>;
    if (status === 'active')   return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Active</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Pending</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Time Clock
              <HelpTooltip text="Employees clock in when starting a shift and clock out when finished. Work hours are recorded automatically and used for timesheets and payroll calculations." learnMoreUrl="/help/time-clock" />
            </h1>
            <p className="text-slate-500 mt-1">
              Track your work hours
            </p>
          </div>
          {/* Android APK download */}
          <a
            href="https://restaff.b-cdn.net/Apps/Android%20Apps/ReStaff.apk"
            target="_blank"
            rel="noopener noreferrer"
            title="Download Android APK"
            className="hover:opacity-80 transition-opacity"
            data-testid="android-apk-link"
          >
            <img
              src="https://restaff.b-cdn.net/Apps/Apps%20Logos/Cloud/apk.webp"
              alt="Download Android APK"
              loading="lazy"
              className="h-12 sm:h-14 w-auto"
            />
          </a>
        </div>

        {/* Clock In/Out Card */}
        <Card className="border-slate-200 overflow-hidden">
          <div className={`p-8 text-center ${activeShift ? 'bg-slate-900' : 'bg-gradient-to-br from-orange-500 to-orange-600'}`}>
            <div className="mb-6">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${
                activeShift ? 'bg-white/10' : 'bg-white/20'
              }`}>
                <Clock className="h-10 w-10 text-white" />
              </div>
            </div>
            
            <p className="text-white/70 text-sm mb-2">
              {activeShift ? 'Currently Working' : 'Ready to Start'}
            </p>
            
            {activeShift && (
              <div className="mb-6">
                <p className="text-5xl font-bold text-white font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {getElapsedTime()}
                </p>
                <p className="text-white/70 text-sm mt-2">
                  Started at {formatTime(activeShift.start_time)}
                </p>
              </div>
            )}
            
            {activeShift ? (
              <ClockOutButton
                onClick={handleClockAction}
                loading={clockLoading}
                size="lg"
              />
            ) : (
              <ClockInButton
                onClick={handleClockAction}
                loading={clockLoading}
                size="lg"
              />
            )}
          </div>
        </Card>

        {/* Pending Approvals (Managers Only) */}
        {canManage && pendingShifts.length > 0 && (
          <Card className="border-slate-200 border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History size={20} className="text-amber-600" />
                Pending Approvals ({pendingShifts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingShifts.map((shift) => (
                  <div
                    key={shift.id}
                    className="flex items-center justify-between p-4 bg-amber-50 rounded-lg"
                    data-testid={`pending-shift-${shift.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{shift.user_name}</p>
                      <p className="text-sm text-slate-600">
                        {formatDate(shift.start_time)} • {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </p>
                      <p className="text-sm text-slate-500">
                        {calculateHours(shift.start_time, shift.end_time)} hours
                        {shift.edited_flag && <span className="ml-2 text-amber-600">(Edited)</span>}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(shift)}
                        className="border-slate-300"
                        data-testid={`edit-shift-${shift.id}`}
                        disabled={actionLoading === shift.id}
                        title="Edit times"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(shift.id)}
                        className="bg-green-500 hover:bg-green-600"
                        data-testid={`approve-shift-${shift.id}`}
                        disabled={actionLoading === shift.id}
                      >
                        {actionLoading === shift.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReject(shift.id)}
                        className="bg-red-500 hover:bg-red-600"
                        data-testid={`reject-shift-${shift.id}`}
                        disabled={actionLoading === shift.id}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Shifts */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History size={20} className="text-slate-600" />
              Recent Shifts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {shifts.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-2 text-slate-500">No shifts recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      {canManage && <th>Staff</th>}
                      <th>Date</th>
                      <th>Time</th>
                      <th>Hours</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map((shift) => (
                      <tr key={shift.id}>
                        {canManage && (
                          <td className="font-medium text-slate-900">{shift.user_name}</td>
                        )}
                        <td>{formatDate(shift.start_time)}</td>
                        <td>
                          {formatTime(shift.start_time)} - {shift.end_time ? formatTime(shift.end_time) : 'Active'}
                        </td>
                        <td>
                          {shift.end_time ? `${calculateHours(shift.start_time, shift.end_time)}h` : '—'}
                        </td>
                        <td>
                          {statusBadge(shift.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Employee Advances (Managers Only) */}
        {canManage && empSummary.length > 0 && (
          <Card className="border-slate-200 border-l-4 border-l-green-500" data-testid="employee-advances-section">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign size={20} className="text-green-600" />
                Outstanding Advances
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Employee</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">Hours (7d)</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">Est. Earnings</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">Advance Balance</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {empSummary.map((emp) => (
                      <tr key={emp.employee_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`emp-summary-row-${emp.employee_id}`}>
                        <td className="py-3 px-3 font-medium text-slate-900">{emp.employee_name}</td>
                        <td className="py-3 px-3 text-right text-slate-600">
                          {(emp.total_work_minutes / 60).toFixed(1)}h
                        </td>
                        <td className="py-3 px-3 text-right text-slate-600">
                          {emp.estimated_earnings != null ? `$${emp.estimated_earnings.toFixed(2)}` : '—'}
                        </td>
                        <td className="py-3 px-3 text-right font-semibold text-amber-700">
                          ${emp.advance_balance.toFixed(2)}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => setDeductModal({ employeeId: emp.employee_id, employeeName: emp.employee_name, amount: '' })}
                            data-testid={`apply-deduction-btn-${emp.employee_id}`}
                          >
                            Apply Deduction
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Times Modal */}
      <Dialog open={!!editState} onOpenChange={(open) => { if (!open) setEditState(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Adjust Times</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="edit-clock-in">Clock In</Label>
              <Input
                id="edit-clock-in"
                type="datetime-local"
                value={editState?.clockIn || ''}
                onChange={(e) => setEditState((s) => ({ ...s, clockIn: e.target.value }))}
                data-testid="edit-clock-in-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-clock-out">Clock Out</Label>
              <Input
                id="edit-clock-out"
                type="datetime-local"
                value={editState?.clockOut || ''}
                onChange={(e) => setEditState((s) => ({ ...s, clockOut: e.target.value }))}
                data-testid="edit-clock-out-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditState(null)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-orange-500 hover:bg-orange-600" data-testid="edit-save-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Apply Deduction Modal */}
      <Dialog open={!!deductModal} onOpenChange={(open) => { if (!open) setDeductModal(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Apply Deduction</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-slate-600">
              Employee: <span className="font-medium">{deductModal?.employeeName}</span>
            </p>
            <div className="space-y-1">
              <Label htmlFor="deduct-amount">Amount to deduct</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <Input
                  id="deduct-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={deductModal?.amount || ''}
                  onChange={(e) => setDeductModal(s => ({ ...s, amount: e.target.value }))}
                  className="pl-7"
                  data-testid="deduction-amount-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeductModal(null)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApplyDeduction}
              disabled={!deductModal?.amount}
              data-testid="deduction-submit-btn"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
